import {useState, useRef, useCallback, useEffect, useMemo} from 'react';
import StoryCollection from "../utils/StoryCollection";

const DURATION = 5000;

export const useStoryCarousel = (stories: StoryCollection) => {
    const storyKeys = useMemo(() => Object.keys(stories), [stories]);
    const [activeStoryKey, setActiveStoryKey] = useState(() => storyKeys[0] || '');
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number>(0);
    const remainingTimeRef = useRef<number>(DURATION);

    const activeStorySlides = useMemo(() => {
        return stories[activeStoryKey] || [];
    }, [stories, activeStoryKey]);

    const resetTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const handleNextSlide = useCallback(() => {
        const currentStoryIndex = storyKeys.indexOf(activeStoryKey);
        const currentStorySlides = stories[activeStoryKey] || [];

        if (activeSlideIndex < currentStorySlides.length - 1) {
            setActiveSlideIndex(prev => prev + 1);
        } else {
            const nextStoryIndex = (currentStoryIndex + 1) % storyKeys.length;
            setActiveStoryKey(storyKeys[nextStoryIndex]);
            setActiveSlideIndex(0);
        }

        startTimeRef.current = Date.now();
        remainingTimeRef.current = DURATION;
    }, [activeStoryKey, activeSlideIndex, storyKeys, stories]);

    const handlePrevSlide = useCallback(() => {
        if (activeSlideIndex > 0) {
            setActiveSlideIndex(prev => prev - 1);
        } else {
            const currentStoryIndex = storyKeys.indexOf(activeStoryKey);
            const prevStoryIndex = (currentStoryIndex - 1 + storyKeys.length) % storyKeys.length;
            const prevStoryKey = storyKeys[prevStoryIndex];
            setActiveStoryKey(prevStoryKey);
            setActiveSlideIndex((stories[prevStoryKey] || []).length - 1);
        }

        startTimeRef.current = Date.now();
        remainingTimeRef.current = DURATION;
    }, [activeSlideIndex, activeStoryKey, stories, storyKeys]);

    const handleTabClick = useCallback((tabKey: string) => {
        if (storyKeys.includes(tabKey)) {
            setActiveStoryKey(tabKey);
            setActiveSlideIndex(0);
            startTimeRef.current = Date.now();
            remainingTimeRef.current = DURATION;
        }
    }, [storyKeys]);

    const pause = useCallback(() => {
        setIsPaused(true);
        resetTimeout();
        const elapsed = Date.now() - startTimeRef.current;
        remainingTimeRef.current = Math.max(0, DURATION - elapsed);
    }, [resetTimeout]);

    const resume = useCallback(() => {
        setIsPaused(false);
        startTimeRef.current = Date.now();
    }, []);

    // Auto advance functionality
    useEffect(() => {
        if (isPaused || storyKeys.length === 0) return;

        resetTimeout();
        startTimeRef.current = Date.now();

        timeoutRef.current = setTimeout(() => {
            handleNextSlide();
        }, remainingTimeRef.current)

        return resetTimeout;
    }, [activeStoryKey, activeSlideIndex, handleNextSlide, resetTimeout, isPaused, storyKeys.length]);

    useEffect(() => {
        if (storyKeys.length > 0 && !storyKeys.includes(activeStoryKey)) {
            setActiveStoryKey(storyKeys[0]);
            setActiveSlideIndex(0);
        }
    }, [storyKeys, activeStoryKey]);

    useEffect(() => {
        return () => {
            resetTimeout();
        };
    }, [resetTimeout]);

    return {
        storyKeys,
        activeStoryKey,
        activeSlideIndex,
        activeStorySlides,
        isPaused,
        handleNextSlide,
        handlePrevSlide,
        handleTabClick,
        pause,
        resume,
        DURATION,
    };
};